

import Chart from "react-apexcharts";
import { useEffect, useState, useMemo, useCallback } from "react";
import { API_RESPONSE } from "../../../utills/app-constant";
import { toastEmitter } from "../../../utills/utilities";
import { overallParticipationDivision } from "../../../services/api-services";
import generateChartImage from "./generateChartImage";
import Remark from "./Remark";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const OverallParticipationChart = ({ selectedCompanyId, graphName }) => {
  const [rowData, setRowData] = useState(null);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [isAttempt, setIsAttempt] = useState(false);

  const [chartSeries, setChartSeries] = useState([]);
  const [categories, setCategories] = useState([]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        id: "overall_participation_division",
        type: "bar",
        stacked: true,
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "35%",
        },
      },
      colors: ["#dc3545", "#ffc107", "#28a745"],
      xaxis: {
        categories: categories,
      },
      yaxis: {
        min: 0,
        max: 60,
        tickAmount: 12,
        labels: {
          formatter: (value) => value,
        },
      },
      dataLabels: {
        enabled: true,
      },
      legend: {
        position: "top",
        onItemClick: {
        toggleDataSeries: false, 
      },
      },
    }),
    [categories]
  );


  // Memoized API Call
  const getAllParticipationDivisionData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await overallParticipationDivision(
        selectedCompanyId,
        graphName
      );
      if (response?.data?.responseCode === 200) {
        const { remark = "", remarkId = null, isAttempt = 0, departmentStats = [] } = response.data;

        setRemark(remark);
        setRemarkId(remarkId);
        setIsAttempt(isAttempt);

        const departmentNames = departmentStats.map((dept) => dept.departmentName);
        const Not_Started = departmentStats.map((dept) => dept.notAttemptedEmployee || 0);
        const In_Progress = departmentStats.map((dept) => dept.inProgressEmployee || 0);
        const Completed = departmentStats.map((dept) => dept.attemptedEmployee || 0);

        setCategories(departmentNames);
        setChartSeries([
          { name: "Not_Started", data: Not_Started },
          { name: "In_Progress", data: In_Progress },
          { name: "Completed", data: Completed },
        ]);
        setRowData(response?.data);
        setDepartmentStats(response?.data?.departmentStats)
      } else {
        setRowData([]);
        if (response?.data?.responseCode !== 404) {
          toastEmitter("error", response?.data?.message);
        }
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, graphName]);


  useEffect(() => {
    getAllParticipationDivisionData();
  }, [getAllParticipationDivisionData]);

  useEffect(() => {
    if (chartSeries.some((series) => series.data.some((val) => val > 0))) {
      setTimeout(() => {
        generateChartImage("overall_participation_division", "overall_participation_division", rowData?.remark);
      }, 100);
    }
  }, [chartSeries, rowData]);

  const exportDepartmentSummaryToExcel = (data, fileName = "Overall Participation (Division)") => {

    if (!data || !Array.isArray(data)) return;

    const title = ["Overall Participation (Division)"];
    const headers = [
      "Department Name",
      "Total Employees",
      "Not Attempted",
      "Attempted",
      "In Progress"
    ];

    const rows = departmentStats?.map(dept => [
      dept.departmentName,
      dept.totalEmployee,
      dept.notAttemptedEmployee,
      dept.attemptedEmployee,
      dept.inProgressEmployee
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([
      title,
      [], // spacing row
      headers,
      ...rows
    ]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Department Summary");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  };

  return (
    <>
      <div className="text-end">
        <button className="px-3 py-2 text-white bg-primary rounded border-0" onClick={() => exportDepartmentSummaryToExcel(departmentStats)}>Download Excel</button>
      </div>
      <div className="overallChart">
        {loading ? (
          <div className="loader-container">
            <div className="loader"></div> {/* Simple CSS loader */}
          </div>
        ) : (
          <div className="chart9" data-heading="Overall Participation (Division)" data-remark={remark}>
            <Chart options={chartOptions} series={chartSeries} type="bar" width="850" />
          </div>
        )}
        <Remark
          graphName="OverallParticipationDivision"
          selectedCompanyId={selectedCompanyId}
          initialRemark={remark}
          remarkId={remarkId}
          isAttempt={isAttempt}
          setRemarkprops={setRemark}
        />
      </div>
    </>

  );
};

export default OverallParticipationChart;
